import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import {Radar} from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import 'chartjs-plugin-dragdata'
import {useSoftwareEvaluationContext} from './SoftwareQualityEvaluationContext'
import {PolygonData} from './apiSoftwareIndicator'

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataLabels
)

type SoftwareIndicatorRadarPlotProps = {
  score_polygon?: PolygonData[]
  minimal_polygon?: PolygonData[]
  software_name?: string,
  display_scores?: boolean,
  callbackOnDrag?: Function,
  showDimensionInitials?: boolean
  showLegend?: boolean
}

export function SoftwareIndicatorRadarPlotChartjs({minimal_polygon, score_polygon, display_scores, callbackOnDrag, showDimensionInitials = false, showLegend = true}: SoftwareIndicatorRadarPlotProps) {

  const ctx = useSoftwareEvaluationContext()
  const minimalPolygon = minimal_polygon ?? ctx.minimalPolygon
  const scorePolygon = score_polygon ?? ctx.scorePolygon

  function PolygonBorderWidth(context: any) {
    const avgSize = Math.round((context.chart.height + context.chart.width) / 2)
    let borderWidth = Math.round(avgSize / 100)
    borderWidth = borderWidth > 5 ? 5 : borderWidth
    return borderWidth
  }

  const minimal_polygon_dataset = {
    label: 'Minimum polygon',
    data: minimalPolygon.map((dimension) => {return dimension.minimum_required_level}),
    borderColor: '#002864',
    backgroundColor: '#00286400',
    borderWidth: function(context: any) {return PolygonBorderWidth(context)} ,
    fill: false,
    pointHitRadius: 250,
    datalabels: {
      display: display_scores ? true : false,
      borderRadius: 25,
      borderWidth: 2,
      padding: 6,
      backgroundColor: function(context: any) {
        return context.dataset.borderColor
      },
      color: 'white',
      formatter: function(value: any, context: any) {
        return ' ' + value + ' '
      }
    }
  }

  const score_polygon_dataset = {
    label: 'Software polygon',
    data: scorePolygon.map((dimension) => {return dimension.minimum_required_level}),
    borderColor: '#05e5ba',
    backgroundColor: '#05e5ba33',
    borderWidth: function(context: any) {return PolygonBorderWidth(context)},
    fill: !scorePolygon.some((dimension) => {return dimension.minimum_required_level === null || dimension.minimum_required_level === undefined}),
    datalabels: {
      display: display_scores ? true : false,
      borderRadius: 25,
      borderWidth: 2,
      padding: 6,
      backgroundColor: function(context: any) {
        return context.dataset.borderColor
      },
      color: 'white',
      formatter: function(value: any, context: any) {
        return ' ' + value + ' '
      }
    }
  }

  const chart_labels = showDimensionInitials ?
    minimalPolygon.map((dimension) => {return ['  ' + dimension.name[0] + '  ', '']})
    : minimalPolygon.map((dimension) => {return [dimension.name, '']})

  const data = {
    labels: chart_labels,
    datasets: scorePolygon.length > 1 ? [score_polygon_dataset, minimal_polygon_dataset] : [minimal_polygon_dataset]
  }

  const options = {
    animation: {
      duration: 0
    },
    scales: {
      r: {
        min: 0,
        max: ctx.indicatorConfigurationMaximumLevel() ?? undefined,
        ticks: {
          stepSize: 1,
          z: 0,
          font: {
            size: 16,
          },
        },
        pointLabels: {
          padding: -8,
          font: function (context: any) {
            const avgSize = Math.round((context.chart.height + context.chart.width) / 2)
            let size = showDimensionInitials ? Math.round(avgSize / 16) : Math.round(avgSize / 32)
            size = size > 16 ? 16 : size
            return {
              size: size,
              weight: 800
            }
          },
          color: '#002864',
        }
      },
    },
    dragData: callbackOnDrag ? true : false,
    dragDataRound: 0,
    plugins: {
      legend: {
        posiition: 'chartArea',
        display: showLegend ? scorePolygon.length > 1 ? true : false : false,
        onClick: scorePolygon.length > 1 ? undefined : () => {},
      },
      datalabels: {
        font: {
          size: 12
        },
        listeners: {
          enter: function(context: any) {
            context.hovered = true
            return true
          },
          leave: function(context: any) {
            context.hovered = false
            return true
          }
        }
      },
      dragData: {
        dragY: callbackOnDrag ? true : false,
        round: 1,
        showTooltip: true,
        onDragEnd: function (event: any, datasetIndex: any, index: any, value: any) {
          console.log(`New value ${value} for ${index} ${datasetIndex}`)
          if (callbackOnDrag) { callbackOnDrag(minimalPolygon[index].id, value) }
        }
      }
    }
  }

  return (
    <Radar data={data} options={options}/>
  )
}
